// ÁREAS E CNAE (04/10/2026) — vista dentro de Contratos: o que está em aberto
// para arrumar. Pedido do Lucas: "na página do contrato, quanto está associado
// a cada área, e uma aba com o que está em aberto para alterar".
//
// Uma linha por contrato que tem algo faltando: divisão por área, parcela sem
// setor (receita fora dos setores na DRE), CNAE do contrato, parcela a faturar
// sem CNAE. Cada linha traz a sugestão do servidor e dois caminhos: aplicar a
// sugestão com um clique, ou abrir a janela e escolher.
//
// Quem classifica, sugere e grava é o servidor (handlers/contratoAreas.js). A
// tela só mostra e pede. Falha ao carregar aparece como FALHA, com o botão de
// tentar de novo — nunca como "nada pendente".
//
// Três cuidados que vieram da revisão de 05/10:
//  • "Aplicar" manda `so_se_vazio`: a lista pode estar velha, e a escolha que
//    alguém fez no meio tempo não pode ser trocada pela sugestão antiga;
//  • o lote aplica o PRINCIPAL antes do aditivo (o aditivo segue o principal);
//  • a carga só grava o plano que o ensaio mostrou (carimbo do ensaio).
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  App as AntApp, Alert, Button, Checkbox, Empty, Flex, Grid, Input, Modal, Popconfirm, Progress,
  Select, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, ReloadOutlined, EditOutlined, CheckOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { STATUS_CONTRATO } from './operacional-contratos/types';
import { fmt } from './operacional-contratos/utils';
import { casaBusca } from '../utils/areasEmpresa';
import {
  LinhaAreas, RespostaPendencias, ItemCarga, PENDENCIAS, CONFIANCA,
  rotuloOrigem, descreverRateio, partesDoRateio, pedidoDaLinha, resumoDoPedido, ordenarLinhas, ordenarParaLote,
} from './operacional-contratos/areasContrato';
import {
  carregarPendenciasDeAreas, definirAreasDoContrato, ensaiarCarga, aplicarCarga, mensagemDeErro, resumoAplicado,
  type RespostaCarga,
} from './operacional-contratos/apiAreas';
import { ModalAreasContrato } from './operacional-contratos/ModalAreasContrato';

type Filtro = 'pendentes' | 'sem_divisao' | 'parcelas_sem_setor' | 'sem_cnae' | 'parcelas_sem_cnae' | 'cnae_divergente' | 'setor_divergente';
const FILTROS: Filtro[] = ['pendentes', 'sem_divisao', 'parcelas_sem_setor', 'sem_cnae', 'parcelas_sem_cnae', 'cnae_divergente', 'setor_divergente'];

// Filtro, busca e "só a faturar" sobrevivem a abrir um contrato e voltar (a
// vista é desmontada ao abrir o contrato): ficam na sessão do navegador.
const CHAVE_FILTROS = 'op:areas:filtros';
function lerFiltros(): { filtro: Filtro; busca: string; soAFaturar: boolean } {
  try {
    const o = JSON.parse(sessionStorage.getItem(CHAVE_FILTROS) || '{}');
    return {
      filtro: FILTROS.includes(o.filtro) ? o.filtro : 'pendentes',
      busca: typeof o.busca === 'string' ? o.busca : '',
      soAFaturar: o.soAFaturar === true,
    };
  } catch { return { filtro: 'pendentes', busca: '', soAFaturar: false }; }
}

export default function OperacionalAreas({ onAbrir }: { onAbrir: (contratoId: string) => void }) {
  return <AntApp><AreasInner onAbrir={onAbrir} /></AntApp>;
}

function TagsRateio({ rateio }: { rateio: Record<string, number> }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 3 }}>
      {partesDoRateio(rateio).map(p => (
        <Tag key={p.chave} style={{ margin: 0, borderColor: p.cor, color: p.cor, background: '#fff' }}>
          {p.proporcao >= 0.9995 ? p.rotulo : `${p.rotulo} ${p.pct}`}
        </Tag>
      ))}
    </span>
  );
}

type EstadoCarga = { aberta: boolean; ensaio: RespostaCarga | null; erro: string | null; rodando: boolean };
const CARGA_FECHADA: EstadoCarga = { aberta: false, ensaio: null, erro: null, rodando: false };

function AreasInner({ onAbrir }: { onAbrir: (id: string) => void }) {
  const { message } = AntApp.useApp();
  // Colunas presas (contrato à esquerda, ações à direita) só em tela larga: no
  // celular as duas juntas são mais largas que a tela e tapariam o miolo.
  const telaLarga = !!Grid.useBreakpoint().lg;
  const inicial = useMemo(lerFiltros, []);
  const [dados, setDados] = useState<RespostaPendencias | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState(inicial.busca);
  const [filtro, setFiltro] = useState<Filtro>(inicial.filtro);
  const [soAFaturar, setSoAFaturar] = useState(inicial.soAFaturar);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [editar, setEditar] = useState<string | null>(null);
  const [aplicando, setAplicando] = useState<string | null>(null);     // id da linha em andamento
  const [lote, setLote] = useState<{ feito: number; total: number; falhas: string[] } | null>(null);
  const [carga, setCarga] = useState<EstadoCarga>(CARGA_FECHADA);
  // Só a resposta do pedido mais recente vale (duas recargas em paralelo: a
  // mais velha chegando por último mostraria linha já resolvida como pendente).
  const pedidoLista = useRef(0);
  const montada = useRef(true);
  useEffect(() => () => { montada.current = false; }, []);
  useEffect(() => {
    try { sessionStorage.setItem(CHAVE_FILTROS, JSON.stringify({ filtro, busca, soAFaturar })); } catch {}
  }, [filtro, busca, soAFaturar]);

  async function carregar() {
    const meu = ++pedidoLista.current;
    setLoading(true); setErro(null);
    try {
      const d = await carregarPendenciasDeAreas();
      if (meu === pedidoLista.current && montada.current) setDados(d);
    } catch (e: any) {
      // Fica o que já estava na tela (se havia) e o erro por cima: uma falha de
      // leitura não pode parecer "está tudo em dia".
      if (meu === pedidoLista.current && montada.current) setErro(mensagemDeErro(e));
    } finally {
      if (meu === pedidoLista.current && montada.current) setLoading(false);
    }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const linhas = useMemo(() => ordenarLinhas(dados?.linhas || []), [dados]);
  const visiveis = useMemo(() => linhas.filter(l => {
    if (filtro === 'pendentes' && !l.pendencias.length) return false;
    if (filtro !== 'pendentes' && !l.pendencias.includes(filtro) && !l.avisos.includes(filtro)) return false;
    if (soAFaturar && !l.numeros.abertas_n) return false;
    if (busca && !casaBusca(busca, `${l.numero} ${l.titulo} ${l.cliente_nome || ''}`)) return false;
    return true;
  }), [linhas, filtro, soAFaturar, busca]);
  const podeEditar = !!dados?.pode_editar;
  const ocupado = !!aplicando || (!!lote && lote.feito < lote.total);
  const aplicaveis = useMemo(() => new Set(visiveis.filter(l => pedidoDaLinha(l)).map(l => l.id)), [visiveis]);
  const marcadas = selecionadas.filter(id => aplicaveis.has(id));

  function abrir(id: string) {
    // O cartão "Áreas e CNAE" mora na Visão geral: é lá que o contrato abre.
    try { sessionStorage.setItem(`op:aba:${id}`, 'visao'); } catch {}
    onAbrir(id);
  }

  async function aplicarLinha(l: LinhaAreas) {
    const pedido = pedidoDaLinha(l);
    if (!pedido || ocupado) return;
    setAplicando(l.id);
    try {
      const r = await definirAreasDoContrato({ ...pedido, so_se_vazio: true });
      // A mensagem conta o que o SERVIDOR fez (a lista podia estar velha).
      const feito = resumoAplicado(r.aplicado);
      if (r.sem_mudanca) message.info(`${l.numero}: já estava em dia (alguém definiu antes) — nada foi alterado.`);
      else message.success(`${l.numero}: áreas e CNAE atualizados${feito ? ' — ' + feito : ''}.`);
      setSelecionadas(s => s.filter(id => id !== l.id));
      await carregar();
    } catch (e: any) {
      message.error(`${l.numero} não foi alterado: ${mensagemDeErro(e)}`);
    } finally { if (montada.current) setAplicando(null); }
  }

  // Aplica a sugestão MOSTRADA em cada linha marcada, uma por vez (cada uma é
  // uma transação no servidor), principal antes do aditivo. O que falhar fica
  // marcado e na lista de falhas, com o motivo.
  async function aplicarMarcadas() {
    const alvo = ordenarParaLote(visiveis.filter(l => marcadas.includes(l.id)));
    if (!alvo.length || ocupado) return;
    const estado = { feito: 0, total: alvo.length, falhas: [] as string[] };
    const idsQueFalharam: string[] = [];
    setLote({ ...estado });
    for (const l of alvo) {
      const pedido = pedidoDaLinha(l);
      try { if (pedido) await definirAreasDoContrato({ ...pedido, so_se_vazio: true }); }
      catch (e: any) { estado.falhas.push(`${l.numero}: ${mensagemDeErro(e)}`); idsQueFalharam.push(l.id); }
      estado.feito++;
      if (montada.current) setLote({ ...estado, falhas: [...estado.falhas] });
    }
    if (!montada.current) return;
    // Sai da seleção só o que foi aplicado; o que falhou (e o que estava
    // marcado mas escondido pelo filtro) continua marcado.
    const aplicados = new Set(alvo.map(l => l.id).filter(id => !idsQueFalharam.includes(id)));
    setSelecionadas(s => s.filter(id => !aplicados.has(id)));
    await carregar();
    if (!estado.falhas.length) { message.success(`${estado.total} contrato(s) atualizados.`); setLote(null); }
  }

  async function abrirCarga() {
    setCarga({ aberta: true, ensaio: null, erro: null, rodando: true });
    try {
      const ensaio = await ensaiarCarga();
      if (montada.current) setCarga(c => (c.aberta ? { aberta: true, ensaio, erro: null, rodando: false } : c));
    } catch (e: any) {
      if (montada.current) setCarga(c => (c.aberta ? { aberta: true, ensaio: null, erro: mensagemDeErro(e), rodando: false } : c));
    }
  }
  async function confirmarCarga() {
    const assinatura = carga.ensaio?.assinatura;
    if (carga.rodando || !assinatura) return;
    setCarga(c => ({ ...c, rodando: true, erro: null }));
    try {
      const r = await aplicarCarga(assinatura);
      const a = r.aplicado!;
      message.success(`Carga aplicada: ${a.divisoes} divisão(ões), ${a.cnaes} CNAE(s) de contrato, ${a.parcelas_setor} parcela(s) com setor e ${a.parcelas_cnae} com CNAE.`, 8);
      setCarga(CARGA_FECHADA);
      await carregar();
    } catch (e: any) {
      // Não dá para saber se gravou (o servidor pode ter concluído depois de a
      // resposta se perder): refaz o ensaio — ele mostra o que AINDA falta — e
      // recarrega a lista, em vez de deixar o plano velho com o botão aceso.
      const mudou = e?.response?.status === 409;
      const msg = mudou
        ? 'Os contratos mudaram desde o ensaio. Refiz a conta: confira o plano novo antes de aplicar.'
        : `${mensagemDeErro(e)} — pode ter sido gravado mesmo assim. Refiz a conta: o plano abaixo é o que ainda falta.`;
      let ensaio: RespostaCarga | null = null;
      try { ensaio = await ensaiarCarga(); } catch { /* fica sem plano: o botão de aplicar apaga */ }
      if (montada.current) setCarga(c => (c.aberta ? { aberta: true, ensaio, erro: msg, rodando: false } : c));
      await carregar();
    }
  }

  const c = dados?.contadores;
  const cartoes: { key: Filtro; n: number; rotulo: string; sub?: string; cor: string; bg: string }[] = c ? [
    { key: 'sem_divisao', n: c.sem_divisao, rotulo: 'Contratos sem divisão por área', cor: '#B91626', bg: '#FDECEC' },
    { key: 'parcelas_sem_setor', n: c.parcelas_sem_setor.n, rotulo: 'Parcelas sem setor',
      sub: c.parcelas_sem_setor.n ? `${fmt(c.parcelas_sem_setor.valor)} em ${c.parcelas_sem_setor.contratos} contrato(s)` : undefined,
      cor: '#B91626', bg: '#FDECEC' },
    { key: 'sem_cnae', n: c.sem_cnae, rotulo: 'Contratos sem CNAE', cor: '#B07500', bg: '#FFF8E8' },
    { key: 'parcelas_sem_cnae', n: c.parcelas_sem_cnae.n, rotulo: 'Parcelas a faturar sem CNAE',
      sub: c.parcelas_sem_cnae.n ? `em ${c.parcelas_sem_cnae.contratos} contrato(s)` : undefined, cor: '#B07500', bg: '#FFF8E8' },
    { key: 'cnae_divergente', n: c.cnae_divergente.n, rotulo: 'CNAE diferente do contrato',
      sub: 'só para conferir', cor: '#0930D0', bg: '#EFF3FF' },
  ] : [];

  // O porquê da etiqueta "conferir"/"palpite" vai escrito na célula (no celular
  // e no teclado não existe "passar o mouse").
  const motivoDa = (s: { motivo: string | null; origem: string }, extra?: string) => (
    <div style={{ fontSize: 10.5, color: '#999', lineHeight: 1.35 }}>
      {rotuloOrigem(s.origem)}{s.motivo ? ` — ${s.motivo}` : ''}{extra || ''}
    </div>
  );

  const colunas: ColumnsType<LinhaAreas> = [
    { title: 'Contrato', key: 'contrato', width: 230, fixed: telaLarga ? 'left' : undefined,
      render: (_, l) => (
        <div role="button" tabIndex={0} style={{ cursor: 'pointer' }} onClick={() => abrir(l.id)}
          onKeyDown={e => { if (e.key === 'Enter') abrir(l.id); }}>
          <Typography.Text strong style={{ fontFamily: 'monospace', color: '#020D7F' }}>{l.numero}</Typography.Text>
          {l.contrato_pai_id && <Tag style={{ marginLeft: 6, fontSize: 10 }} color="gold">aditivo</Tag>}
          <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 210 }}>{l.titulo}</div>
          <div style={{ fontSize: 10.5, color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 210 }}>{l.cliente_nome || '—'}</div>
        </div>
      ) },
    { title: 'Status', key: 'status', width: 120,
      render: (_, l) => {
        const s = (STATUS_CONTRATO as any)[l.status];
        return s ? <Tag style={{ background: s.bg, color: s.cor, border: 'none', fontWeight: 600 }}>{s.label}</Tag> : <Tag>{l.status}</Tag>;
      } },
    // Sem coluna "o que falta": cada coluna já diz o que falta NELA (a sugestão
    // no lugar da divisão/CNAE, a contagem em vermelho nas parcelas). Uma coluna
    // de etiquetas repetiria as três.
    { title: 'Divisão por área', key: 'divisao', width: 330,
      render: (_, l) => {
        if (l.divisao) {
          return (
            <div>
              <TagsRateio rateio={l.divisao} />
              <div style={{ fontSize: 10.5, color: '#999' }}>
                {l.divisao_herdada ? `segue o principal ${l.pai_numero || ''}` : rotuloOrigem(l.divisao_origem)}
              </div>
            </div>
          );
        }
        const s = l.sugestao.divisao;
        if (!s) {
          return (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <Tag color="red" style={{ marginRight: 4 }}>falta</Tag>
              {l.contrato_pai_id
                ? <>defina no principal{' '}
                    <Button type="link" size="small" style={{ padding: 0, height: 18 }} onClick={() => abrir(l.contrato_pai_id!)}>{l.pai_numero}</Button></>
                : 'sem sugestão — escolha em Editar'}
            </Typography.Text>
          );
        }
        return (
          <div>
            <Tag color={CONFIANCA[s.confianca].cor} style={{ marginRight: 4 }}>sugestão {CONFIANCA[s.confianca].rotulo}</Tag>
            <TagsRateio rateio={s.rateio} />
            {motivoDa(s, s.alternativa ? ` · alternativa: ${descreverRateio(s.alternativa)}` : '')}
          </div>
        );
      } },
    { title: 'CNAE', key: 'cnae', width: 230,
      render: (_, l) => {
        if (l.cnae) {
          return (
            <div>
              <Typography.Text strong>{l.cnae}</Typography.Text>
              <div style={{ fontSize: 10.5, color: '#999' }}>{l.cnae_herdado ? `do principal ${l.pai_numero || ''}` : l.cnae_manual ? 'escolhido à mão' : ''}</div>
            </div>
          );
        }
        const s = l.sugestao.cnae;
        if (!s) return <Typography.Text type="secondary" style={{ fontSize: 12 }}><Tag color="orange" style={{ marginRight: 4 }}>falta</Tag>sem sugestão</Typography.Text>;
        return (
          <div>
            <Tag color={CONFIANCA[s.confianca].cor} style={{ marginRight: 4 }}>sugestão {CONFIANCA[s.confianca].rotulo}</Tag>
            <Typography.Text strong>{s.codigo}</Typography.Text>
            {motivoDa(s)}
          </div>
        );
      } },
    { title: 'Parcelas', key: 'parcelas', width: 210,
      render: (_, l) => (
        <div style={{ fontSize: 12 }}>
          {l.numeros.sem_setor_n > 0 && <div style={{ color: '#B91626' }}>{l.numeros.sem_setor_n} sem setor{l.numeros.sem_setor_valor != null ? ` · ${fmt(l.numeros.sem_setor_valor)}` : ''}</div>}
          {l.avisos.includes('setor_divergente') && <div style={{ color: '#0930D0' }}>parcelas de outra área — decidir em Editar</div>}
          {l.numeros.abertas_sem_cnae_n > 0 && <div style={{ color: '#B07500' }}>{l.numeros.abertas_sem_cnae_n} a faturar sem CNAE</div>}
          {l.numeros.cnae_divergente_n > 0 && <div style={{ color: '#0930D0' }}>{l.numeros.cnae_divergente_n} com CNAE diferente</div>}
          {!l.numeros.sem_setor_n && !l.numeros.abertas_sem_cnae_n && !l.numeros.cnae_divergente_n && !l.avisos.includes('setor_divergente') && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {l.numeros.parcelas ? `${l.numeros.parcelas} parcela(s), em dia` : 'sem parcelas'}
            </Typography.Text>
          )}
        </div>
      ) },
    ...(podeEditar ? [{
      title: '', key: 'acoes', width: 170, fixed: telaLarga ? 'right' as const : undefined,
      render: (_: unknown, l: LinhaAreas) => {
        const pedido = pedidoDaLinha(l);
        return (
          <Flex gap={4}>
            {pedido && (
              <Popconfirm
                title={`Aplicar em ${l.numero}?`} description={<div style={{ maxWidth: 320 }}>{resumoDoPedido(l, pedido)}</div>}
                okText="Aplicar" cancelText="Cancelar" onConfirm={() => aplicarLinha(l)} disabled={ocupado}>
                <Button size="small" type="primary" ghost icon={<CheckOutlined />} loading={aplicando === l.id} disabled={ocupado && aplicando !== l.id}>
                  Aplicar
                </Button>
              </Popconfirm>
            )}
            <Button size="small" icon={<EditOutlined />} disabled={ocupado} onClick={() => setEditar(l.id)}>Editar</Button>
          </Flex>
        );
      },
    }] : []),
  ];

  const colunasCarga: ColumnsType<ItemCarga> = [
    { title: 'Contrato', key: 'c', width: 210,
      render: (_, p) => <div><b style={{ fontFamily: 'monospace' }}>{p.numero}</b><div style={{ fontSize: 11, color: '#888' }}>{p.titulo}</div></div> },
    { title: 'Divisão por área', key: 'd',
      render: (_, p) => p.divisao ? <div><TagsRateio rateio={p.divisao.rateio} /><div style={{ fontSize: 10.5, color: '#999' }}>{rotuloOrigem(p.divisao.origem)}</div></div> : <span style={{ color: '#bbb' }}>não muda</span> },
    { title: 'CNAE', key: 'n', width: 130,
      render: (_, p) => p.cnae ? <div><b>{p.cnae.codigo}</b><div style={{ fontSize: 10.5, color: '#999' }}>{rotuloOrigem(p.cnae.origem)}</div></div> : <span style={{ color: '#bbb' }}>não muda</span> },
    { title: 'Parcelas', key: 'p', width: 250,
      render: (_, p) => (
        <div style={{ fontSize: 12 }}>
          {p.parcelas_setor && <div><b>{p.parcelas_setor.n}</b> recebem setor ({descreverRateio(p.parcelas_setor.rateio)}) · {fmt(p.parcelas_setor.valor)}</div>}
          {p.parcelas_cnae && <div><b>{p.parcelas_cnae.n}</b> a faturar recebem CNAE {p.parcelas_cnae.cnae}</div>}
          {!p.parcelas_setor && !p.parcelas_cnae && <span style={{ color: '#bbb' }}>nenhuma</span>}
        </div>
      ) },
  ];

  return (
    <div style={{ padding: '12px 24px 24px' }}>
      {erro && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="Não consegui carregar as pendências de áreas e CNAE"
          description={<>{erro}{dados ? ' — o que está abaixo é da última leitura que deu certo.' : ''}</>}
          action={<Button size="small" onClick={carregar} loading={loading}>Tentar de novo</Button>} />
      )}

      {c && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
          {cartoes.map(k => {
            const alternar = () => setFiltro(filtro === k.key ? 'pendentes' : k.key);
            return (
              <div key={k.key} role="button" tabIndex={0} aria-pressed={filtro === k.key}
                onClick={alternar} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } }}
                style={{ cursor: 'pointer', padding: '8px 14px', borderRadius: 8,
                  background: k.bg, border: filtro === k.key ? `2px solid ${k.cor}` : '2px solid transparent' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: k.cor }}>{k.n}</div>
                <div style={{ fontSize: 11.5, color: '#444' }}>{k.rotulo}</div>
                {k.sub && <div style={{ fontSize: 10.5, color: '#777' }}>{k.sub}</div>}
              </div>
            );
          })}
        </div>
      )}

      <Flex gap={8} wrap="wrap" style={{ marginBottom: 12 }} align="center">
        <Input prefix={<SearchOutlined />} placeholder="Contrato, título ou cliente" allowClear
          value={busca} onChange={e => setBusca(e.target.value)} style={{ width: 260 }} />
        <Select<Filtro> value={filtro} onChange={setFiltro} style={{ minWidth: 250 }} showSearch
          filterOption={(d, o) => casaBusca(d, o?.label)}
          options={[
            { value: 'pendentes', label: 'Tudo o que falta' },
            ...Object.entries(PENDENCIAS).map(([k, v]) => ({ value: k as Filtro, label: v.rotulo + (v.aviso ? ' (conferir)' : '') })),
          ]} />
        <Checkbox checked={soAFaturar} onChange={e => setSoAFaturar(e.target.checked)}>Só com parcela a faturar</Checkbox>
        <div style={{ flex: 1 }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{visiveis.length} contrato(s)</Typography.Text>
        {podeEditar && marcadas.length > 0 && (
          <Popconfirm
            title={`Aplicar a sugestão mostrada em ${marcadas.length} contrato(s)?`}
            description={<div style={{ maxWidth: 340 }}>Cada contrato recebe a divisão e o CNAE sugeridos na linha dele, e as parcelas sem setor / a faturar sem CNAE são preenchidas. Sugestão marcada "conferir" também entra — desmarque o que não conferiu. Quem já tiver sido definido por outra pessoa não é alterado.</div>}
            okText="Aplicar" cancelText="Cancelar" onConfirm={aplicarMarcadas} disabled={ocupado}>
            <Button type="primary" icon={<CheckOutlined />} disabled={ocupado}>Aplicar nas {marcadas.length} marcadas</Button>
          </Popconfirm>
        )}
        {dados?.pode_carga && dados.carga.contratos > 0 && (
          <Tooltip title="Mostra, antes de gravar, tudo o que as sugestões seguras mudariam">
            <Button icon={<ThunderboltOutlined />} onClick={abrirCarga} disabled={ocupado}>
              Aplicar as sugestões prontas ({dados.carga.contratos} contratos)
            </Button>
          </Tooltip>
        )}
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading} disabled={ocupado} aria-label="Recarregar" />
      </Flex>

      {lote && (
        <Alert type={lote.falhas.length ? 'warning' : 'info'} showIcon style={{ marginBottom: 12 }}
          closable={lote.feito === lote.total} onClose={() => setLote(null)}
          message={lote.feito < lote.total ? `Aplicando… ${lote.feito} de ${lote.total}` : `${lote.total - lote.falhas.length} de ${lote.total} contrato(s) atualizados — os que falharam continuam marcados`}
          description={<>
            {lote.feito < lote.total && <Progress percent={Math.round((lote.feito / lote.total) * 100)} size="small" />}
            {lote.falhas.map(f => <div key={f} style={{ fontSize: 12 }}>✗ {f}</div>)}
          </>} />
      )}

      {loading && !dados ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : dados ? (
        <Table<LinhaAreas>
          size="small" rowKey="id" columns={colunas} dataSource={visiveis}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          rowSelection={podeEditar ? {
            selectedRowKeys: marcadas,
            // Mantém o que estava marcado e hoje está escondido pelo filtro.
            onChange: (keys) => setSelecionadas(s => [...s.filter(id => !aplicaveis.has(id)), ...(keys as string[])]),
            getCheckboxProps: (l) => ({
              disabled: !aplicaveis.has(l.id) || ocupado,
              title: aplicaveis.has(l.id) ? undefined : 'Sem sugestão para aplicar com um clique — use Editar',
            }),
          } : undefined}
          locale={{ emptyText: <Empty description={filtro === 'pendentes' && !busca && !soAFaturar ? 'Nada em aberto: todo contrato com divisão por área e CNAE, e toda parcela com setor.' : 'Nenhum contrato no filtro'} /> }}
        />
      ) : null}

      {/* `key` por contrato: a janela nasce limpa a cada abertura (nada do
          contrato anterior fica para trás). */}
      <ModalAreasContrato
        key={editar || 'nenhum'}
        contratoId={editar} aberto={!!editar}
        onFechar={() => setEditar(null)}
        onSalvo={() => { setEditar(null); carregar(); }} />

      <Modal
        open={carga.aberta} title="Aplicar as sugestões prontas"
        // Enquanto GRAVA não fecha; enquanto só calcula o ensaio, pode desistir.
        onCancel={() => { if (!(carga.rodando && carga.ensaio)) setCarga(CARGA_FECHADA); }}
        width="94vw" style={{ maxWidth: 1100, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
        footer={[
          <Button key="x" onClick={() => setCarga(CARGA_FECHADA)} disabled={carga.rodando && !!carga.ensaio}>Cancelar</Button>,
          <Button key="ok" type="primary" onClick={confirmarCarga} loading={carga.rodando && !!carga.ensaio}
            disabled={!carga.ensaio || !carga.ensaio.plano?.length}>
            Aplicar em {carga.ensaio?.resumo.contratos ?? 0} contrato(s)
          </Button>,
        ]}>
        {carga.erro && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Não deu certo" description={carga.erro}
            action={!carga.ensaio && !carga.rodando ? <Button size="small" onClick={abrirCarga}>Tentar de novo</Button> : undefined} />
        )}
        {carga.rodando && !carga.ensaio && <div style={{ textAlign: 'center', padding: 40 }}><Spin /> <div style={{ marginTop: 8, color: '#888' }}>Calculando o que mudaria (nada é gravado ainda)…</div></div>}
        {carga.ensaio && (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Isto é o que será gravado — nada mudou ainda"
              description={
                <div style={{ fontSize: 12.5 }}>
                  <b>{carga.ensaio.resumo.divisoes}</b> contrato(s) ganham divisão por área ·{' '}
                  <b>{carga.ensaio.resumo.cnaes}</b> ganham CNAE ·{' '}
                  <b>{carga.ensaio.resumo.parcelas_setor}</b> parcela(s) sem setor recebem setor ({fmt(carga.ensaio.resumo.parcelas_setor_valor)}) ·{' '}
                  <b>{carga.ensaio.resumo.parcelas_cnae}</b> parcela(s) a faturar recebem CNAE.
                  <div style={{ marginTop: 4, color: '#666' }}>
                    Só entram sugestões seguras: a divisão que as parcelas já usam (ou a dos serviços vendidos) e o CNAE da venda ou das parcelas.
                    Nenhuma parcela que já tem setor ou CNAE é alterada, e nota emitida não é tocada. Cada mudança fica no histórico da parcela.
                    Entram também contratos já concluídos que só não tinham a divisão gravada — por isso o número é maior que o da lista.
                    Se algum contrato mudar enquanto você confere, a gravação é recusada e a conta é refeita.
                  </div>
                </div>
              } />
            <Table<ItemCarga> size="small" rowKey="id" columns={colunasCarga} dataSource={carga.ensaio.plano || []}
              pagination={{ pageSize: 100, showSizeChanger: false }}
              scroll={{ y: 'clamp(220px, calc(100vh - 430px), 620px)' as any }} />
          </>
        )}
      </Modal>
    </div>
  );
}
